import { contactPath, groupPath } from 'helper/recordPaths';
import { Building2Icon, CalendarDaysIcon, ChevronDown, ClipboardListIcon, Mail, Pencil, Plus } from 'assets/icons';
import Badge from 'components/badge/badge';
import Button from 'components/button';
import CardIconButton from 'components/card/CardIconButton';
import resendInvitation from 'components/record/resendInvitation';
import MenuButton from 'components/DropdownMenu/MenuButton';
import Tooltip from 'components/tooltips/Tooltips';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import useModal from 'components/modal/useModal';
import RecordActivity from 'components/record/activity/RecordActivity';
import { callAction, callNumbers, useDial } from 'components/record/callAction';
import { buildActivity } from 'components/record/activity/activityItems';
import ConfirmDialog from 'components/record/ConfirmDialog';
import CompanyContacts from 'components/record/CompanyContacts';
import ExchangeList from 'components/record/ExchangeList';
import { contactLabel } from 'components/record/InlineContactField';
import KpiTiles from 'components/record/KpiTiles';
import NextActionCard from 'components/record/NextActionCard';
import QuickActions from 'components/record/QuickActions';
import RecordFields from 'components/record/RecordFields';
import RecordIdentity, { RecordIcon } from 'components/record/RecordIdentity';
import RecordLayout, { RecordLayoutSkeleton, RecordSection, RecordTabs } from 'components/record/RecordLayout';
import RecordMissions from 'components/record/RecordMissions';
import { isMissionCompany, missionDataFor } from 'components/record/missionCompany';
import { apiErrorText, errorCodeOf } from 'helper/apiErrorText';
import ResultsCard from 'components/record/ResultsCard';
import useOpenPanel from 'components/record/useOpenPanel';
import SlideOverCompanyInvoicesTab from 'components/SlideOver/dataSlideOver/data/slideOverCompany/SlideOverCompanyInvoicesTab';
import SlideOverCompanyRemunerationConditions from 'components/SlideOver/dataSlideOver/data/slideOverCompany/slideOverCompanyRemunerationConditions';
import { PAYMENT_SETTINGS_TARGET } from 'components/SlideOver/dataSlideOver/data/slideOverCompany/slideOverCompany';
import { MISSION_TEMPLATES_CHANGED } from 'components/SlideOver/dataSlideOver/data/slideOverAddMission/slideOverAddMission';
import { COMPLETE_CLIENT_PANEL } from 'components/companies/CompleteClientPanel';
import { EDIT_COMPANY_PANEL } from 'components/companies/CompanyEditPanel';
import { ADD_COMPANY_CONTACT_MODAL } from 'components/modal/dataModal/data/modalCompany/ModalAddCompanyContact';
import { DELETE_COMPANY_MODAL } from 'components/modal/dataModal/data/modalCompany/ModalDeleteCompany';
import useSlideOver from 'components/SlideOver/useSlideOver';
import { canCreateMission } from 'components/sidebar/sidebarLinks';
import { clientSizeLabel } from 'constants/clientSizes';
import { useOnChange } from 'hooks/useChangeSignal';
import UseContext from 'hooks/useContext';
import useAppointmentPanel from 'hooks/useAppointmentPanel';
import useCompanyFilterOptions from 'hooks/useCompanyFilterOptions';
import useCrmContext from 'hooks/useCrmContext';
import Usefetchdata from 'hooks/usefetchdata';
import useLatestCall from 'hooks/useLatestCall';
import useRecordChanged from 'hooks/useRecordChanged';
import useRecordPanels from 'hooks/useRecordPanels';
import useTaskPanels from 'hooks/useTaskPanels';
import useTasksChanged from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import { canChangeStage, stageMovesOf } from 'helper/crmStageRights';
import { COMMERCIAL_ROLE, SUPER_ADMIN_ROLE, canProspect, hidesProspection } from 'helper/roles';
import { isoDay } from 'helper/timeLabels';
import PillTabs from 'components/tabs/PillTabs';
import useRemunerationDraft from 'hooks/useRemunerationDraft';
import { confirmLeaving } from 'hooks/useUnsavedChangesWarning';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiDataBase from 'server';
import crmApi from 'server/crm/crmApi';
import { addToCallDay } from 'server/crm/prospectionApi';
import { departmentLabel, dateLabel } from './companiesItems';
import { STAGE_BADGES, exchangeWhen, exchangesOf, formatSiret, fullName, kpiTiles, nextActionView, prospectTiles, taskBoardOf, wholeEuros } from './recordItems';

const TABS = ['overview', 'activity', 'contacts', 'missions', 'billing', 'pay'];
const TASK_BOARD_EMPTY = {
  todo: { title: 'Rien de prévu', description: 'Les tâches, rappels et rendez-vous à venir s’afficheront ici.' },
  late: { title: 'Rien en retard', description: 'Les tâches et rappels dont l’heure est passée s’afficheront ici.' },
  done: { title: 'Aucune tâche terminée', description: 'Les tâches cochées s’afficheront ici.' },
};

/**
 * The page of a company, as on the prototype: on the left who it is, its
 * actions, what comes next and what we know of it; on the right its numbers
 * and its tabs (overview, activity, contacts, missions, billing, pay
 * conditions). Jobbiz gives the company, its members, its missions, its
 * invoices and its pay conditions; its activity and its results come from
 * server/crm (crmRecords.js), its CRM state from admin/crm/company/crm.
 * `?onglet=` opens a tab.
 */
// A client stays a new one (« Nouvelles ») until it is moved by hand, with
// its recruiter (JBWEB-1423): no delay moves it on its own.
const isNewClient = (state) => state?.stage === 'client' && !state.oldClientSince;

// A company deleted, or that never was: the page says it is missing.
const isCompanyGone = (error) => error?.response?.status === 404 || errorCodeOf(error) === 'COMPANY_NOT_FOUND';

/**
 * The CRM state of the record, the api's alone (admin/crm/company/crm); a
 * failed read leaves it empty. clientSince is already a Martinique day
 * « YYYY-MM-DD »: going through new Date() would read it as UTC midnight, the
 * day before west of UTC.
 */
const crmOf = (answer) => {
  const crm = answer?.crm ?? {};
  return {
    stage: crm.stage ?? null,
    clientSince: crm.clientSince ? String(crm.clientSince).slice(0, 10) : null,
    oldClientSince: crm.oldClientSince ?? null,
    commercialId: crm.commercialId ?? null,
    recruiterId: crm.recruiterId ?? null,
    lostAt: crm.lostAt ?? null,
    lostReason: crm.lostReason ?? null,
    lostDetails: crm.lostDetails ?? '',
    relaunchOn: crm.relaunchOn ?? null,
    email: crm.email ?? '',
    phone: crm.phone ?? '',
    size: crm.size ?? null,
    sizeSource: crm.sizeSource ?? null,
    etp: crm.etp ?? 0,
    source: crm.source ?? null,
    sourceNetwork: crm.sourceNetwork ?? null,
    referrer: crm.referrer ?? null,
    nextAction: answer?.nextAction ?? null,
  };
};

/**
 * How the prospect was found: its source, with the network of a « social »
 * one or the person who recommended a « referral » one. Null without a source.
 */
const sourceValue = (crm, { text, prospectionText }) => {
  if (!crm.source) return null;
  const source = prospectionText(`sources.${crm.source}`);
  if (crm.source === 'social' && crm.sourceNetwork) {
    return `${source} · ${prospectionText(`networks.${crm.sourceNetwork}`)}`;
  }
  const referrer = crm.source === 'referral' ? crm.referrer : null;
  const name = [referrer?.firstname, referrer?.lastname].filter(Boolean).join(' ');
  if (!name) return source;
  const reach = [referrer.email, contactLabel('phone', referrer.phone)].filter(Boolean);
  return (
    <>
      <span className="block">{source}</span>
      <span className="block">{text('referredBy', { name })}</span>
      {reach.map((line) => (
        <span key={line} className="block text-gray-700">
          {line}
        </span>
      ))}
    </>
  );
};

const CompanyPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Who opened the page may ask to come back to it (the list on its tab, a
  // task); an address typed or shared comes back to the list.
  const { state: locationState } = useLocation();
  const backTo = typeof locationState?.backTo === 'string' ? locationState.backTo : '/company';
  const [params] = useSearchParams();
  const { text, language } = useText('record');
  const dial = useDial();
  const { text: activityText } = useText('recordActivity');
  const backLabel = backTo.startsWith('/tasks') ? language.getValue('tasks.titleMine') : text('backCompanies');
  const { text: sizeText } = useText('clientSizes');
  const { text: prospectionText } = useText('prospection');
  const { call, ready } = useCrmContext();
  // Only the last read lands: a newer one cancels it, which is no error.
  const latest = useLatestCall();
  const { userData } = Usefetchdata();
  const { setObjID, setTarget, setModalData, setDataCreateMission, setMissionTemplateToOpen, askCompleteClientForMission } = UseContext();
  const { toggleSlideOver } = useSlideOver();
  const openPanel = useOpenPanel();
  const { toggle } = useModal();
  const { openRecordForm } = useRecordPanels();
  const { openTask, openTaskForm } = useTaskPanels();
  const { openAppointment } = useAppointmentPanel();
  const { categories } = useCompanyFilterOptions();
  const canSeeInvoices = Boolean(userData?.accessInvoice);
  const prospects = !hidesProspection(userData?.role);
  const tabs = TABS.filter((tab) => tab !== 'billing' || canSeeInvoices);
  const [record, setRecord] = useState(null);
  // « Créer une mission » on a lost company, waiting for its confirmation.
  const [confirmingLostMission, setConfirmingLostMission] = useState(false);
  // { gone: true } for a company deleted, else { message }.
  const [failed, setFailed] = useState(null);
  const [tab, setTab] = useState(() => (tabs.includes(params.get('onglet')) ? params.get('onglet') : 'overview'));
  // The remuneration changes held back on the « pay » tab (JBWEB-1468): leaving
  // the tab drops them, once the user agreed.
  const payDraft = useRemunerationDraft(id);
  const switchTab = (next) => {
    if (tab === 'pay' && next !== 'pay') {
      if (!confirmLeaving(payDraft.changes.length > 0)) return;
      payDraft.discard();
    }
    setMissionsView('missions');
    setTab(next);
  };
  const [missionsView, setMissionsView] = useState(params.get('vue') === 'interimaires' ? 'workers' : 'missions');
  const [taskTab, setTaskTab] = useState('todo');
  const [companyInfo, setCompanyInfo] = useState(null);
  // The CRM state the api holds (admin/crm/company/crm): whether the client
  // is still a new one, and its recruiter.
  const [crmState, setCrmState] = useState(null);
  const [crmContacts, setCrmContacts] = useState([]);
  // Its job descriptions (« Missions › Fiches de poste »), as the mission
  // panel lists them; none when they cannot be read.
  const [templates, setTemplates] = useState([]);

  const load = useCallback(
    () =>
      latest(Promise.all([
        call(crmApi.getCompanyRecord, { companyId: id, canSeeInvoices }),
        // A failed read leaves the record without its CRM state (no next action,
        // no stage); a company gone (404) is missing.
        call(crmApi.getCompanyCrm, { companyId: id }).then(
          (answer) => answer ?? null,
          (error) => {
            if (isCompanyGone(error)) throw error;
            return null;
          },
        ),
      ])).then(
        ([loaded, answer]) => {
          setCrmState(answer?.crm ?? null);
          // A contact linked to the account its invitation created shows as that member;
          // so does one whose address is a member's (the admin created by « Passer en
          // client » from the prospect's first contact is not linked to it).
          const memberEmails = new Set(
            loaded.members.map((member) => (member.email ?? '').toLowerCase()).filter(Boolean),
          );
          setCrmContacts(
            (answer?.contacts ?? []).filter(
              (contact) => !contact.userId && !memberEmails.has((contact.email ?? '').toLowerCase()),
            ),
          );
          // The position and the lead status of a member are the api's; a member
          // whose place is deactivated is no longer one of the contacts.
          const positions = answer?.memberPositions ?? {};
          const leadStatuses = answer?.memberLeadStatuses ?? {};
          const deactivated = new Set((answer?.deactivatedMemberIds ?? []).map(String));
          const next = {
            ...loaded,
            members: loaded.members
              .filter((member) => !deactivated.has(String(member.id)))
              .map((member) => ({ ...member, position: positions[member.id] ?? '', leadStatus: leadStatuses[member.id] ?? '' })),
            crm: crmOf(answer),
          };
          setRecord(next);
          setCompanyInfo(next.company);
          setFailed(null);
        },
        (error) => setFailed(isCompanyGone(error) ? { gone: true } : { message: error.message }),
      ),
    [call, latest, id, canSeeInvoices],
  );
  useEffect(() => {
    if (ready) load();
  }, [load, ready]);
  useRecordChanged(load);
  useTasksChanged(load);
  // A member added or invited from the admin's modal.
  useOnChange('employee_created', load);
  // A stage move shown before the api answers (« Passer en ancien client »):
  // the record and its CRM state take `patch` until `load` reads the api again.
  const showMove = useCallback((patch) => {
    setCrmState((state) => (state ? { ...state, ...patch } : state));
    setRecord((shown) => (shown ? { ...shown, crm: { ...shown.crm, ...patch } } : shown));
  }, []);
  const loadTemplates = useCallback(() => {
    const token = localStorage.getItem('xsrfToken');
    if (!token) return;
    ApiDataBase.getAdminMissionTemplates(
      { token, idCompany: id },
      (data) => setTemplates(data?.templates ?? []),
      () => setTemplates([]),
    );
  }, [id]);
  useEffect(loadTemplates, [loadTemplates]);
  useOnChange(MISSION_TEMPLATES_CHANGED, loadTemplates);

  const team = useMemo(() => record?.team ?? [], [record]);
  // `fallback`: what names someone the team does not know (« - » for a Wildix call).
  const nameOf = useCallback(
    (personId, fallback = text('someone')) => {
      const person = team.find((member) => member.id === personId);
      return person ? fullName(person) : fallback;
    },
    [team, text],
  );
  const now = new Date();
  const items = useMemo(
    () => (record ? buildActivity(record.history, { now: new Date(), text: activityText, nameOf }) : []),
    [record, activityText, nameOf],
  );

  if (failed) {
    return (
      <RecordLayout side={null}>
        <CompactEmptyState
          glyph="searchX"
          title={text('companyMissing')}
          description={failed.gone ? text('companyMissingText') : failed.message}
          action={{ label: backLabel, onClick: () => navigate(backTo) }}
        />
      </RecordLayout>
    );
  }
  if (!record) return <RecordLayoutSkeleton tabs={6} />;

  const { company, group, members, crm } = record;
  const moves = stageMovesOf(crmState, userData);
  // Without the api's CRM state, no stage is shown.
  let stageLabel = '';
  if (crm.stage === 'client') stageLabel = text(crm.oldClientSince ? 'stage_oldClient' : 'stage_newClient');
  else if (crm.stage) stageLabel = text(`stage_${crm.stage}`);
  const name = company.name_commercial || company.reason_social || '';
  // Its people: the members with an account, then the contacts without one.
  // Each keeps where it comes from, for the forms' contactRef: a member is an
  // account (userId), a CRM contact a sub-document (contactId).
  const contacts = [
    ...members.map((person) => ({ id: person.id, userId: person.id, name: fullName(person), email: person.email, phone: person.phone })),
    ...crmContacts.map((person) => ({ id: person.id, contactId: person.id, name: fullName(person), email: person.email, phone: person.phone })),
  ];
  // The api answers the switchboard, else the company's own number.
  const companyPhone = crm.phone;
  const openForm = (kind, extra = {}) =>
    openRecordForm(kind, { companyId: id, recordName: name, recordEmail: crm.email ?? '', contacts, ...extra });
  // « Appel » has Wildix dial the number, which logs the call.
  const noPhone = () => toast.info(text('noPhone'));
  const callFirst = () => {
    const [first] = callNumbers([...contacts, { name, phone: companyPhone }]);
    if (first) dial(first.phone, id);
    else noPhone();
  };
  // The company panel, on the tab of what is edited: its overview (« Modifier
  // l'entreprise »), or straight on its « Paramètres de paiement » (the
  // coefficients).
  const openCompanyPanel = (tab = 'overview') => {
    setObjID(id);
    setTarget(tab);
    toggleSlideOver('get_company');
  };
  const missionCompany = {
    companyId: String(company._id ?? id),
    companyName: company.name_commercial || '',
    idLocation: company.location ?? null,
  };
  // « Créer une mission »: the mission form, on this company already. A
  // prospect gets none from the api: its profile is completed first, then the
  // form opens (CompleteClientForMissionDialog, JBWEB-1456). So does a lost
  // company that never was a client (no BeSTT number); a lost former client
  // takes its mission once the move is confirmed, and the api makes it a new
  // client again as the mission is created (retours du 31/10/2026).
  const openMissionForm = () => {
    setDataCreateMission(missionDataFor(missionCompany));
    toggleSlideOver('add_mission');
  };
  const createMission = () => {
    if (crm.stage === 'prospect' || (crm.stage === 'lost' && !company.id_bestt)) {
      askCompleteClientForMission(missionCompany);
      return;
    }
    if (crm.stage === 'lost') {
      setConfirmingLostMission(true);
      return;
    }
    openMissionForm();
  };
  // A job description opens in the mission panel, on this company, as when
  // picked there: a mission starts from it, or it is changed.
  const openTemplate = (template) => {
    setDataCreateMission(missionDataFor(missionCompany));
    setMissionTemplateToOpen({ template, siblings: templates });
    toggleSlideOver('add_mission');
  };
  // A number at the top opens the tab it comes from.
  const openTab = (next) => () => switchTab(next);
  const tileActions = {
    missions: openTab('missions'),
    ...(tabs.includes('billing') && { revenue: openTab('billing'), unpaid: openTab('billing') }),
  };
  const addToCalls = () =>
    addToCallDay({ companyId: id }).then(
      () => toast.success(text('addedToCalls')),
      (error) => toast.error(apiErrorText(error, { language, text: prospectionText, group: 'errors' })),
    );
  // How to reach the company itself: its general address and its switchboard
  // (crm.email, crm.phone), the e-mail's first recipient and the first number called.
  // A move without a form (« Repasser en nouveau client », « Remettre en
  // prospect »): a toast, then the page read again for its badge and actions.
  const moveStage = (method, saved, errors) =>
    call(method, { companyId: id }).then(
      () => {
        toast.success(text(saved));
        load();
      },
      (error) => toast.error(apiErrorText(error, { language, text, group: errors })),
    );
  // « Passer client »: the form of a client company, filled with the
  // prospect's values, which every field of must be filled to send.
  // « Modifier l'entreprise »: every setting of the company in one form.
  // Its number and e-mail go with it: the panel shows them as the page does.
  const editCompany = () => {
    setObjID({
      id,
      phone: companyPhone,
      email: crm.email ?? '',
      canEditEmail: canEditContacts,
      size: crm.size ?? null,
      sizeFromEtp: crm.sizeSource === 'etp',
      commercialId: crm.commercialId ?? null,
    });
    toggleSlideOver(EDIT_COMPANY_PANEL);
  };
  const completeClient = () => {
    setObjID(id);
    toggleSlideOver(COMPLETE_CLIENT_PANEL);
  };
  // The contacts without an account are written by the agency manager and the
  // sales reps only, for now (as the groups' contacts).
  const canEditContacts = [SUPER_ADMIN_ROLE, COMMERCIAL_ROLE].includes(Number(userData?.role));
  // An invitation on Jobbiz, filled with a contact's values: one without an
  // account is linked to the account created, by its id.
  const addMember = (prefill = null) => {
    const filled = prefill && { email: prefill.email, firstName: prefill.firstname, lastName: prefill.lastname, phone: prefill.phone };
    if (!prefill) setModalData({ idCompany: id });
    else if (prefill.source === 'crm') setModalData({ idCompany: id, prefill: filled, companyContactId: prefill.id });
    else setModalData({ idCompany: id, prefill: filled });
    toggle('add_company_member');
  };
  // « Ajouter un contact »: by default one without an account, which its
  // modal can invite on Jobbiz (JBWEB-1424); with `contact`, it is edited.
  const addContact = (contact = null) => {
    setModalData({ idCompany: id, ...(contact ? { contact } : {}) });
    toggle(ADD_COMPANY_CONTACT_MODAL);
  };
  const resend = (person) => resendInvitation({ companyId: id, memberId: person.id, email: person.email });
  const add = (kind) => {
    if (kind === 'task') openTaskForm({ defaults: { type: 'todo', companyId: id, scoped: true } });
    else openForm(kind === 'all' ? 'note' : kind === 'event' ? 'event' : kind);
  };
  const openItem = (ref) => {
    // A client's action on a mission opens that mission.
    if (ref.kind === 'client' && ref.missionId) return openPanel('get_mission', ref.missionId);
    if (ref.kind === 'task') openTask(ref.id);
    // A call-list entry opens its call sheet in Prospection.
    if (ref.kind === 'callback') navigate(`/prospection?appeler=${id}`);
    // A note on a Wildix call, linked to the company.
    if (ref.kind === 'phoneCall') openForm('note', { phoneCallId: ref.id, links: [ref.link] });
    if (ref.kind === 'client') switchTab(ref.tab);
  };

  // What comes next, and the two buttons that act on it.
  const firstContact = contacts[0]?.name ?? '';
  const next = nextActionView(crm.nextAction, { text, contactName: firstContact, language, now });
  let nextButtons;
  if (next?.kind === 'task') {
    nextButtons = {
      primary: { label: text('openTask'), onClick: () => openTask(crm.nextAction.id) },
      secondary: { label: text('postpone'), onClick: () => openTaskForm({ taskId: crm.nextAction.id }) },
    };
  } else if (next?.kind === 'callback') {
    // A callback belongs to the prospecting call list.
    nextButtons = prospects
      ? { primary: { label: text('call'), onClick: callFirst } }
      : {};
  } else if (next) {
    nextButtons = {
      primary: { label: text('outcome'), onClick: () => openForm('note', { contactName: firstContact, outcome: true }) },
      // The same appointment moved: its form, every field filled.
      secondary: {
        label: text('postpone'),
        onClick: () =>
          crm.nextAction.id
            ? openAppointment(crm.nextAction.id, { edit: true })
            : openForm('event', { contactName: firstContact }),
      },
    };
  }

  const category = categories.find((item) => String(item.id) === String(company.pennylane_category_id));
  const address = [company.address?.street, company.address?.postcode, company.address?.city].filter(Boolean).join(', ');
  const created = record.summary?.createdAt ? dateLabel(isoDay(new Date(record.summary.createdAt)), now) : '';
  const fields = [
    { label: text('fieldLegalName'), value: company.reason_social },
    {
      label: text('fieldGroup'),
      value: group && (
        <button type="button" onClick={() => navigate(groupPath(group.id))} className="text-left hover:underline">
          {group.name}
        </button>
      ),
    },
    { label: text('fieldAddress'), value: address || departmentLabel(record.summary?.department) },
    // Written from « Modifier l'entreprise » (the pencil).
    { label: text('fieldNumber'), value: contactLabel('phone', companyPhone) },
    { label: text('fieldEmail'), value: contactLabel('email', crm.email) },
    { label: text('fieldSiret'), value: company.siret ? formatSiret(company.siret) : '' },
    { label: text('fieldSupplier'), value: company.companySupplier?.name },
    { label: text('fieldApe'), value: company.code_ape },
    { label: text('fieldLegalForm'), value: company.status },
    { label: text('fieldStaff'), value: company.staff ? text('staff', { count: company.staff }) : '' },
    { label: text('fieldCapital'), value: company.capital != null && company.capital !== '' ? wholeEuros(company.capital) : '' },
    {
      label: text('fieldAgreement'),
      value: company.collectiveAgreement?.id ? `${company.collectiveAgreement.name ?? 'IDCC'} (IDCC ${company.collectiveAgreement.id})` : '',
    },
    { label: text('fieldCategory'), value: category?.label },
    {
      label: sizeText('label'),
      // A client with running missions takes its size from them (read only);
      // otherwise it is chosen here, « Aucune » only off the client stage.
      value: crm.sizeSource === 'etp' ? (
        <Tooltip text={sizeText('fromEtp')} placement="top">
          <span className="cursor-default">
            {clientSizeLabel(language, crm.size) || '-'} · {sizeText('etpToday', { etp: crm.etp })}
          </span>
        </Tooltip>
      ) : (
        // Chosen in « Modifier l'entreprise » (the pencil).
        clientSizeLabel(language, crm.size)
      ),
    },
    {
      label: text(crm.stage === 'client' ? 'fieldClientSince' : 'fieldCreated'),
      value: crm.stage === 'client' && crm.clientSince ? dateLabel(crm.clientSince, now) : created,
    },
    // Nobody assigned: a dash, not « Quelqu’un ».
    { label: text('fieldCommercial'), value: crm.commercialId ? nameOf(crm.commercialId, '') : '' },
  ];
  const source = sourceValue(crm, { text, prospectionText });
  if (source) fields.push({ label: text('fieldSource'), value: source });

  // The tasks have their own card: the exchanges keep what was said and done.
  const { recent } = exchangesOf(items.filter((item) => !['task', 'callback'].includes(item.kind)));
  const board = taskBoardOf(items);
  // « À faire » holds the late ones too, in red at its top.
  const boardTab = taskTab;
  const exchange = (item) => ({
    key: item.key,
    title: item.title,
    detail: item.body,
    when: exchangeWhen(item),
    late: item.late,
    onOpen: item.ref ? () => openItem(item.ref) : undefined,
  });

  return (
    <RecordLayout
      side={
        <>
          <RecordIdentity
            back={{ label: backLabel, onClick: () => navigate(backTo) }}
            menu={
              <MenuButton
                trigger={(toggleMenu) => (
                  <Button color="SECONDARY" size="SM" onClick={toggleMenu}>
                    <span>{text('actions')}</span>
                    <ChevronDown wh={18} color="currentColor" />
                  </Button>
                )}
                items={[
                  { label: text('editCompany'), onClick: editCompany },
                  // A prospect becomes a client once its sheet is complete, by the
                  // agency manager or its own commercial (JBWEB-1422); a lost one
                  // goes back to prospect first.
                  crmState?.stage === 'prospect' &&
                    (userData?.role === SUPER_ADMIN_ROLE ||
                      (Boolean(crmState.commercialId) && String(crmState.commercialId) === String(userData?._id))) && {
                      label: text('toClient'),
                      onClick: completeClient,
                    },
                  // A new client becomes an old one with its recruiter (JBWEB-1423).
                  // The moves are the agency manager's and the company's commercial's.
                  moves.toOld &&
                    isNewClient(crmState) && {
                      label: text('toOld'),
                      onClick: () =>
                        openForm('toOld', {
                          recruiterId: crmState.recruiterId,
                          commercialId: crmState.commercialId,
                          onMoved: showMove,
                        }),
                    },
                  moves.toNew && { label: text('toNew'), onClick: () => moveStage(crmApi.moveCompanyToNew, 'stageNew', 'toNewErrors') },
                  moves.markLost && { label: text('markLost'), onClick: () => openForm('lost', { commercialId: crmState.commercialId ?? null }) },
                  moves.toProspect && {
                    label: text('toProspect'),
                    onClick: () => moveStage(crmApi.reactivateProspect, 'stageProspect', 'toProspectErrors'),
                  },
                  canProspect(userData?.role) &&
                    crmState?.stage === 'prospect' && { label: text('addToCalls'), onClick: addToCalls },
                  // A company already in a group stays a company of it. The api lets
                  // the agency manager or the company's own commercial do it (JBWEB-1443).
                  !group &&
                    (userData?.role === SUPER_ADMIN_ROLE ||
                      (userData?.role === COMMERCIAL_ROLE &&
                        Boolean(crmState?.commercialId) &&
                        String(crmState.commercialId) === String(userData?._id))) && {
                      label: text('toGroup'),
                      onClick: () => openForm('toGroup'),
                    },
                  // Any company, whatever its stage, and only by the agency manager:
                  // it is archived, never deleted, and restored from the console.
                  userData?.role === SUPER_ADMIN_ROLE && {
                    label: text('deleteCompany'),
                    onClick: () => {
                      setModalData({ companyId: id, recordName: name });
                      toggle(DELETE_COMPANY_MODAL);
                    },
                    className: 'text-red-600',
                  },
                ]}
              />
            }
            picture={<RecordIcon icon={Building2Icon} />}
            title={<span className="uppercase">{name}</span>}
            badge={crm.stage ? <Badge type={STAGE_BADGES[crm.stage]}>{stageLabel}</Badge> : null}
            subtitle={[company.id_bestt && text('number', { number: company.id_bestt }), group && text('inGroup', { name: group.name })]
              .filter(Boolean)
              .join(' · ')}
          >
            <QuickActions
              actions={[
                { key: 'note', label: text('actionNote'), icon: Pencil, onClick: () => openForm('note') },
                { key: 'email', label: text('actionEmail'), icon: Mail, onClick: () => openForm('email') },
                callAction({ label: text('actionCall'), people: [{ name, phone: companyPhone }, ...contacts], onNone: noPhone, dial: (phone) => dial(phone, id) }),
                { key: 'task', label: text('actionTask'), icon: ClipboardListIcon, onClick: () => add('task') },
                { key: 'event', label: text('actionMeeting'), icon: CalendarDaysIcon, onClick: () => openForm('event') },
              ]}
            />
          </RecordIdentity>
          {/* Nothing planned: no card at all. */}
          {next && (
            <NextActionCard
              label={text('nextAction')}
              title={next.title}
              detail={next.detail}
              {...nextButtons}
            />
          )}
          <RecordSection className="ls:flex ls:min-h-0 ls:flex-1 ls:flex-col">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-900">{text('about')}</p>
              <CardIconButton label={text('editCompany')} icon={<Pencil wh={18} color="#374151" />} onClick={editCompany} />
            </div>
            <div className="mt-2 ls:flex ls:min-h-0 ls:flex-1 ls:flex-col">
              <RecordFields fields={fields} />
            </div>
          </RecordSection>
        </>
      }
    >
      {/* Missions, revenue, invoices for a client; a prospect (or a lost
          company), which has none, shows its rendez-vous and its calls. */}
      {isMissionCompany(crm.stage) ? (
        <KpiTiles tiles={kpiTiles({ ...record.kpis, stage: crm.stage }, text)} actions={tileActions} />
      ) : (
        <KpiTiles tiles={prospectTiles({ results: record.results, calls: record.history?.calls })} />
      )}
      <RecordTabs
        tabs={tabs.map((one) => ({ id: one, label: text(`tab_${one}`) }))}
        activeTab={tab}
        onChange={switchTab}
      />
      {tab === 'overview' && (
        <div className="flex flex-col gap-4 pb-6">
          {/* Results that could not be read: no card. */}
          {/* A prospect's results are its tiles above. */}
          {record.results && isMissionCompany(crm.stage) && <ResultsCard results={record.results} />}
          <div className="grid grid-cols-1 gap-4 tab:grid-cols-2">
            <ExchangeList
              title="Tâches"
              header={
                <PillTabs
                  activeTab={boardTab}
                  onChange={setTaskTab}
                  variant="counter"
                  greyZero
                  tabs={[
                    { id: 'todo', label: 'À faire', count: board.todo.length },
                    { id: 'late', label: 'En retard', count: board.late.length },
                    { id: 'done', label: 'Terminées', count: board.done.length },
                  ]}
                />
              }
              items={board[boardTab].slice(0, 5).map(exchange)}
              empty={{ icon: CalendarDaysIcon, ...TASK_BOARD_EMPTY[boardTab] }}
            />
            <ExchangeList
              title={text('recent')}
              items={recent.map(exchange)}
              empty={{ glyph: 'inbox', title: text('recentEmptyTitle'), description: text('recentEmptyText') }}
            />
          </div>
        </div>
      )}
      {tab === 'activity' && (
        <RecordActivity
          items={items}
          onAdd={add}
          onOpen={openItem}
        />
      )}
      {tab === 'contacts' && (
        <CompanyContacts
          members={members}
          // The contacts without an account: the company's own (JBWEB-1424).
          unregistered={crmContacts.map((contact) => ({ ...contact, source: 'crm' }))}
          group={group}
          groupContacts={record.groupContacts}
          onOpen={(person) => navigate(contactPath(person.id))}
          onAdd={canEditContacts ? () => addContact() : null}
          onInvite={addMember}
          onResend={resend}
          companyId={id}
          onRoleChanged={() => {
            toast.success(text('roleSaved'));
            load();
          }}
          onOpenGroup={() => navigate(groupPath(group.id))}
          onOpenGroupContact={(person) => navigate(contactPath(person.id))}
        />
      )}
      {tab === 'missions' && (
        <RecordMissions
          key={missionsView}
          missions={record.missions.missions}
          tempWorkers={record.missions.tempWorkers}
          templates={templates}
          // Always on a company, even without any: the view says so.
          showTemplates
          // From a client's page only, to whoever creates missions (as « Créer une mission »).
          onOpenTemplate={canCreateMission(userData) && isMissionCompany(crm.stage ?? 'client') ? openTemplate : undefined}
          initial={missionsView}
          action={
            canCreateMission(userData) &&
            // Without the api's stage (a failed read), the company counts as a client,
            // as the api does (it still refuses a prospect, COMPANY_NOT_CLIENT). A
            // prospect or a lost company is offered it too, to whoever may make it
            // a client (as « Passer en client »).
            (isMissionCompany(crm.stage ?? 'client') ||
              (['prospect', 'lost'].includes(crm.stage) && canChangeStage(crm, userData))) && (
              <Button color="SECONDARY" size="SM" onClick={createMission}>
                <Plus wh={18} color="currentColor" />
                <span>{text('createMission')}</span>
              </Button>
            )
          }
        />
      )}
      {tab === 'billing' && canSeeInvoices && (
        <div className="flex flex-col gap-4 pb-6 text-sm">
          <SlideOverCompanyInvoicesTab companyInfo={company} userData={userData} />
        </div>
      )}
      {tab === 'pay' && companyInfo && (
        <div className="flex flex-col gap-4 pb-6 text-sm">
          <SlideOverCompanyRemunerationConditions
            companyInfo={companyInfo}
            setCompanyInfo={setCompanyInfo}
            setIsShowCoeff={() => openCompanyPanel(PAYMENT_SETTINGS_TARGET)}
            setIsCoeffSend={() => {}}
            onSaved={load}
          />
        </div>
      )}
      <ConfirmDialog
        open={confirmingLostMission}
        title="Créer une mission pour cette entreprise perdue ?"
        description="Elle repassera en nouveau client dès que la mission sera créée."
        cancelLabel="Annuler"
        confirmLabel="Continuer"
        onCancel={() => setConfirmingLostMission(false)}
        onConfirm={() => {
          setConfirmingLostMission(false);
          openMissionForm();
        }}
      />
    </RecordLayout>
  );
};

export default CompanyPage;
